import { toast } from '@askable/ui/core/sonner';
import _ from 'lodash';
import { useState } from 'react';
import { useMutation } from 'urql';

import { withAppContext } from 'components/HOCS';
import { CancelParticipantSession } from 'data/mutations/bookingSubmission/cancelParticipantSession.mutation';
import { InviteParticipantSession } from 'data/mutations/bookingSubmission/inviteParticipantSession.mutation';
import { ReInviteParticipantSession } from 'data/mutations/bookingSubmission/reInviteParticipantSession.mutation';
import { getCombinedErrorMessage } from 'utils/error_utils';

import InviteParticipant from './inviteParticipant';
import ReInviteParticipant from './reInviteParticipant';

import type React from 'react';

import './invitationDialogStyles.scss';

type IProps = {
  booking: any;
  singleSession: { participant: any; participant_session_id: string; session: any };
  onFinishInvitationProcess: any;
  link_id: any;
  onCloseInvitationDialog: any;
  onRefreshTable: any;
};

function InvitationDialog(props: IProps): React.ReactElement<IProps> {
  const { booking, singleSession, onFinishInvitationProcess } = props;

  const [loading, setLoading] = useState(false);
  const [showConfirmationMessage, setShowConfirmationMessage] = useState(false);

  const [, reInviteParticipantSession] = useMutation(ReInviteParticipantSession);
  const [, cancelParticipantSession] = useMutation(CancelParticipantSession);
  const [, inviteParticipantSession] = useMutation(InviteParticipantSession);

  const onConfirmInvitation = async () => {
    const participantSessions = _.get(singleSession, 'participant.booking_participant');
    const participantWaitlistSessions = _.filter(participantSessions, (item: any) => item.status === 2);
    setLoading(true);
    const cancelResults = await Promise.all(
      participantWaitlistSessions.map((session: any) => {
        return cancelParticipantSession({
          booking_id: booking._id,
          participant_session_id: _.get(session, '_id'),
        });
      }),
    );

    const cancelError = cancelResults.find(r => r.error)?.error;
    if (cancelError) {
      toast.error(getCombinedErrorMessage(cancelError, 'Error cancelling waitlisted session'));
      setLoading(false);
      return;
    }

    const result = await inviteParticipantSession({
      booking_id: booking._id,
      participant_session_id: props.singleSession.participant_session_id,
      online_task_link_id: props.link_id,
    });
    if (result.error) {
      toast.error(getCombinedErrorMessage(result.error, 'Error saving study'));
      setLoading(false);
      return;
    }

    // Stops the loading state and start the confirmation message animation
    setLoading(false);
    setShowConfirmationMessage(true);
  };

  const onConfirmReInvitation = async () => {
    const { participant } = props.singleSession;
    setLoading(true);

    const result = await reInviteParticipantSession({
      booking_id: booking._id,
      participant_session_id: _.get(participant, 'invitedSession._id'),
    });
    if (result.error) {
      toast.error(getCombinedErrorMessage(result.error, 'Error saving study'));
      setLoading(false);
      return;
    }

    // Stops the loading state and start the confirmation message animation
    setLoading(false);
    setShowConfirmationMessage(true);
  };

  // Parent component to deal with all different scenarios to invite participants
  //  It accepts either single invitation and bulk invitation
  //  If it gets a 'single_session' object as a prop then it means the client is trying to invite just one participant
  //  If it gets a 'bulk_session' object as a prop then it means the client is trying to inbite multiple participants for sessions

  const hasBeenInvited = _.has(props, 'singleSession.participant.invitedSession');
  if (hasBeenInvited) {
    return (
      <ReInviteParticipant
        {...props}
        loading={loading}
        // @ts-expect-error ts-migrate(2554) FIXME: Expected 0 arguments, but got 1.
        onConfirm={(callback: any) => onConfirmReInvitation(callback)}
        onFinishInvitation={() => {
          // Close the confirmation message and after closes the sessions container
          setShowConfirmationMessage(false);
          // Custom callback after finishing everything
          if (onFinishInvitationProcess) onFinishInvitationProcess();

          if (props.onRefreshTable) props.onRefreshTable();
        }}
        showConfirmationMessage={showConfirmationMessage}
      />
    );
  }

  return (
    <InviteParticipant
      {...props}
      loading={loading}
      onConfirm={onConfirmInvitation}
      onFinishInvitation={() => {
        // Close the invitation dialog
        if (props.onCloseInvitationDialog) props.onCloseInvitationDialog();
        // Custom callback after finishing everything
        if (props.onFinishInvitationProcess) props.onFinishInvitationProcess();
        // Close the confirmation message and after closes the sessions container
        // setState({ showConfirmationMessage: false }, () => {
        //     if (props.onCloseSessionsContainer) props.onCloseSessionsContainer();
        // });
        setShowConfirmationMessage(false);

        if (props.onRefreshTable) props.onRefreshTable();
      }}
      showConfirmationMessage={showConfirmationMessage}
    />
  );
}

export default withAppContext(InvitationDialog);
